import React, { useEffect, useState } from 'react';
import { createContext } from 'react';
import { useStoreDetails } from '../StoreDetailsProvider/UseStoreDetails';
import { getHeaderData, getTableData, getAvaliableSheet, getPricesByProductFamily } from '../../../api/Product';
import { calculatePricedEquipmentData, getPriceTableByBrandAndDate } from '../../../reservation/CalcPrice';
import { formatDateString } from '../../Utils';


export interface VehicleReservationMainProps {
  pickup: Date | null; // start date
  dropoff: Date | null; // end date
  prices: {
    subtotal: number;
    tax: number;
    discount: number;
    service_fee: number;
    total: number;
  },
  price_table_id: number | null;
  name: string;
  note: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  state_id: number;
  postal_code: string;
  phone_number: string;
  email: string;
  password: string;
  is_accept: boolean;
  img_url: string,
  address_id: number | null,
  selectedAddress: any,
  use_manual: boolean;
  manual_address: string,
  usps_address: string,
  mapbox_id: string | null,
  lat: number | null,
  lng: number | null,
  driver_tip: number,
  discount_code: string,
  promo_code: number | null,
  discount_rate: number | null,
  headerData: Array<any>,
  priceTableData: Array<any>,
  availableSheet: any,
  license_file: File | null;
  additional_license_files: File[] | null;
  insurance_file: File | null;
  additional_insurance_files: File[] | null;
  agreement_file: File | null,
  pickup_time: string | null, // start time
  dropoff_time: string | null, // end time
  discountApplied: boolean,
  bodhi_protect_opted_out: boolean,
  bodhi_protect_price: number,
  
  // Driving Questions
  is_only_driver: boolean | null;
  additional_drivers: { name: string; email: string; phone: string }[] | null;
  confirm_drivers: boolean;
  speed_limit_agreement: boolean;
  damage_responsibility: boolean;
  return_charged: boolean;
  
  // Rental Selection
  mainArea: string,
  subArea: string,
  orderType: string,
  hotel: string,
  deliveryAddr: string,
  mainCategory: string,
  delivery_charge: number,
  packageData: Array<any>, // Store API response with package details
  selectedDuration: string | null, // Currently selected duration ID
  no_available_time_slots: boolean, // Flag for when no time slots are available
  upcharge: number | null, // Extra charge for specific durations (e.g., off-island delivery)
  upcharge_plantation: string | null, // Name of plantation/location for upcharge
  plantation: string | null, // Plantation name from selected address
  plantation_id: number | null, // Plantation ID from selected address
}

interface ContextProps {
  VehicleReservationMain: VehicleReservationMainProps;
  VehicleReservationItems: Array<any>;
  isCalculating: boolean;
  setVehicleReservationMain: (VehicleReservationMain: VehicleReservationMainProps) => void;
  setVehicleReservationValue: (key: keyof VehicleReservationMainProps, value: any) => void;
  getVehicleReservationValue: (key: keyof VehicleReservationMainProps) => any | null;
  addVehicleReservationItems: (items: Array<any>, orderType?: string) => void;
  removeVehicleReservationItem: (index: number) => void;
  clearVehicleReservationItems: () => void;
  initVehicleReservation: () => void;
  updatePricingForSelectedDuration: (selectedDurationId: string | null, durationData?: any) => void;
}

const initializedVehicleMain: VehicleReservationMainProps = {
  pickup: null,
  dropoff: null,
  prices: {
    subtotal: 0,
    tax: 0,
    discount: 0,
    service_fee: 0,
    total: 0,
  },
  price_table_id: null,
  name: "",
  note: "",
  address: "",
  address2: "",
  city: "",
  state: "",
  state_id: 0,
  postal_code: "",
  phone_number: "",
  email: "",
  password: "",
  is_accept: false,
  img_url: "",
  address_id: null,
  selectedAddress: null,
  use_manual: false,
  manual_address: "",
  usps_address: "",
  mapbox_id: null,
  lat: null,
  lng: null,
  driver_tip: 0.00,
  discount_code: "",
  promo_code: null,
  discount_rate: null,
  headerData: [],
  priceTableData: [],
  availableSheet: null,
  license_file: null,
  additional_license_files: null,
  insurance_file: null,
  additional_insurance_files: null,
  agreement_file: null,
  pickup_time: null,
  dropoff_time: null,
  discountApplied: false,
  bodhi_protect_opted_out: false,
  bodhi_protect_price: 0,

  // Driving Questions
  is_only_driver: null,
  additional_drivers: null,
  confirm_drivers: false,
  speed_limit_agreement: false,
  damage_responsibility: false,
  return_charged: false,

  // Vehicle Rental Selection
  mainArea: "",
  subArea: "",
  orderType: "",
  hotel: "",
  deliveryAddr: "",
  mainCategory: "",
  delivery_charge: 0.00,
  packageData: [],
  selectedDuration: null,
  no_available_time_slots: false,
  upcharge: null,
  upcharge_plantation: null,
  plantation: null,
  plantation_id: null,
}

export const VehicleReservationContext = createContext<ContextProps>({
  isCalculating: false,
  VehicleReservationMain: initializedVehicleMain,
  VehicleReservationItems: [],
  setVehicleReservationMain: () => {},
  setVehicleReservationValue: () => {},
  getVehicleReservationValue: () => null,
  addVehicleReservationItems: () => {},
  removeVehicleReservationItem: () => {},
  clearVehicleReservationItems: () => {},
  initVehicleReservation: () => {},
  updatePricingForSelectedDuration: () => {},
});

export const VehicleReservationProvider = ({ children }:{children:React.ReactNode}) => {
  const { storeDetails, discounts, priceLogicData } = useStoreDetails();

  const [VehicleReservationMain, setVehicleReservationMain] = useState<VehicleReservationMainProps>(initializedVehicleMain);
  const [VehicleReservationItems, setVehicleReservationItems] = useState<Array<any>>([]);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  const setVehicleReservationValue = (key: keyof VehicleReservationMainProps, value: any) => {
    setVehicleReservationMain((prev) => {
      if (prev) {
        return { ...prev, [key]: value };
      } else {
        return { ...initializedVehicleMain, [key]: value }
      }
    });
  };

  const getVehicleReservationValue = (key: keyof VehicleReservationMainProps): any | null => {
    return VehicleReservationMain ? VehicleReservationMain[key] : null;
  };

  const addVehicleReservationItems = (items:Array<any>, orderType?: string) => {
    if (items.length === 0) return
    
    if (orderType) {
      setVehicleReservationValue("orderType", orderType);
    }
    
    const updatedItems = [...VehicleReservationItems, ...items];
    
    // Pricing now comes from category_id via getBusyPeriods API, not family_id
    // Skip the old pricing API call - prices will be set when duration is selected
    setVehicleReservationItems(updatedItems);
    console.log('✅ Items added to cart (prices will be set on duration selection):', updatedItems);
  }

  const removeVehicleReservationItem = (index:number) => {
    const updatedItems =  [...VehicleReservationItems];
    updatedItems.splice(index, 1);
    setVehicleReservationItems(updatedItems);
    calculateFinalPrices(updatedItems);
  }

  const clearVehicleReservationItems = () => {
    setVehicleReservationItems([]);
    // Reset prices to zero
    setVehicleReservationValue('prices', {
      subtotal: 0,
      tax: 0,
      discount: 0,
      service_fee: 0,
      total: 0,
    });
  }

  const updatePricingForSelectedDuration = (selectedDurationId: string | null, durationData?: any) => {
    if (!selectedDurationId || VehicleReservationItems.length === 0) {
      return;
    }

    const updatedItems = VehicleReservationItems.map((item) => {
      // If durationData is provided from new API, use it directly
      if (durationData && durationData.price) {
        const itemPrice = durationData.price * (item.quantity || 1);
        console.log('🔍 updatePricingForSelectedDuration (NEW API):', {
          durationId: selectedDurationId,
          durationData_price: durationData.price,
          durationData_upcharge: durationData.upcharge,
          itemPrice,
          quantity: item.quantity || 1
        });
        return {
          ...item,
          price: itemPrice,
          price_id: durationData.price_id,
          battery_percent: durationData.battery_percent,
          base_price: durationData.price,
          selected_pricing: durationData
        };
      }

      // Fallback to old pricing_info if durationData not provided
      if (!item.pricing_info || !item.pricing_info.prices) {
        return item;
      }

      // Parse the selected duration to get duration value and type
      const durationMatch = selectedDurationId.match(/^(\d+)-(hour|day|week)$/);
      if (!durationMatch) {
        return item;
      }

      const [, durationValue, durationType] = durationMatch;
      const targetDuration = parseInt(durationValue);
      
      // Find matching price option from the item's pricing data
      let selectedPrice = null;
      
      if (durationType === 'hour') {
        selectedPrice = item.pricing_info.prices.find((price: any) => 
          price.duration_type === 'hourly' && price.duration_value === targetDuration
        );
      } else if (durationType === 'day') {
        selectedPrice = item.pricing_info.prices.find((price: any) => 
          price.duration_type === 'daily' && price.duration_value === targetDuration
        );
      } else if (durationType === 'week') {
        selectedPrice = item.pricing_info.prices.find((price: any) => 
          price.duration_type === 'weekly' && price.duration_value === targetDuration
        );
      }
      if (selectedPrice) {
        const itemPrice = selectedPrice.price * (item.quantity || 1);
        console.log('🔍 updatePricingForSelectedDuration (OLD API):', {
          durationId: selectedDurationId,
          selectedPrice_price: selectedPrice.price,
          selectedPrice_upcharge: selectedPrice.upcharge,
          itemPrice,
          quantity: item.quantity || 1
        });
        return {
          ...item,
          price: itemPrice,
          price_id: selectedPrice.id,
          battery_percent: selectedPrice.battery_percent,
          base_price: selectedPrice.price,
          selected_pricing: selectedPrice
        };
      }

      return item;
    });

    console.log('🔍 Setting updated items with prices:', updatedItems);
    setVehicleReservationItems(updatedItems);
    calculateFinalPrices(updatedItems);
  }

  const calcAndSetVehicleData = async (VehicleReservationItems:Array<any>, providedOrderType?: string) =>{
    if (VehicleReservationItems.length === 0) return
    setIsCalculating(true);
    
    // Find items that don't have pricing data yet
    const itemsWithoutPricing = VehicleReservationItems.filter(item => !item.pricing_info);
    
    // Prepare items for API call - group by family_id and sum quantities for items without pricing
    const apiItems = itemsWithoutPricing.reduce((acc: any[], item) => {
      const existingItem = acc.find(accItem => accItem.family_id === item.family_id);
      if (existingItem) {
        existingItem.quantity += (item.quantity || 1);
      } else {
        acc.push({
          family_id: item.family_id,
          quantity: item.quantity || 1
        });
      }
      return acc;
    }, []);

    // Use provided orderType or fall back to current state
    const effectiveOrderType = providedOrderType || VehicleReservationMain.orderType;
    
    // Don't proceed if order type is not set
    if (!effectiveOrderType) {
      console.warn('calcAndSetVehicleData: Order type not set, skipping API call');
      setIsCalculating(false);
      return;
    }

    // Map order type to API order type using current state
    let apiOrderType: string = ''; // Default value
    switch (effectiveOrderType) {
      case 'ride_off':
        apiOrderType = 'pickup';
        break;
      case 'delivery':
        apiOrderType = 'delivery';
        break;
    }

    const payload = {
      brand_id: storeDetails?.brand_id || 0,
      order_type: apiOrderType,
      items: apiItems
    };

    // Call the API to get pricing only for items without pricing data
    let apiPricingData: any[] = [];
    if (apiItems.length > 0) {
      try {
        const response = await new Promise((resolve, reject) => {
          getPricesByProductFamily(payload, (jsonRes: any, status: any, error: any) => {
            if (status === 200 && jsonRes) {
              resolve(jsonRes);
            } else {
              reject(error || 'API call failed');
            }
          });
        });
        apiPricingData = response as any[];
      } catch (error) {
        console.error('Error calling getPricesByProductFamily:', error);
        setIsCalculating(false);
        return;
      }
    }

    // Calculate rental duration if dates are available
    let rentalDuration: number | null = null;
    if (VehicleReservationMain.pickup && VehicleReservationMain.dropoff) {
      rentalDuration = new Date(VehicleReservationMain.dropoff).getTime() - new Date(VehicleReservationMain.pickup).getTime();
    }

    const updatedItems = VehicleReservationItems.map((item) => {
      // If item already has pricing info, keep it as is
      if (item.pricing_info) {
        return item;
      }

      // Find pricing data for this family_id
      const familyPricing = apiPricingData.find((pricing: any) => pricing.family_id === item.family_id);
      
      if (!familyPricing || !familyPricing.prices || familyPricing.prices.length === 0) {
        // No pricing data available, return item without pricing
        return {
          ...item,
          price: 0,
          base_price: 0,
          pricing_info: null
        };
      }

      // Select the best pricing option based on rental duration for initial display
      let selectedPrice: any = null;
      
      if (rentalDuration && rentalDuration > 0) {
        // Find the pricing option that best matches the rental duration
        // Select the option with the smallest duration that covers the rental period
        selectedPrice = familyPricing.prices
          .filter((price: any) => price.milliseconds >= rentalDuration!)
          .sort((a: any, b: any) => a.milliseconds - b.milliseconds)[0] || 
          familyPricing.prices[familyPricing.prices.length - 1]; // Fallback to longest duration
      } else {
        // No rental duration, use the first pricing option
        selectedPrice = familyPricing.prices[0];
      }

      // Calculate price based on quantity and the selected pricing option
      const itemPrice = selectedPrice ? (selectedPrice.price * (item.quantity || 1)) : 0;
      
      return {
        ...item,
        price: itemPrice,
        base_price: selectedPrice ? selectedPrice.price : 0,
        price_id: selectedPrice.id,
        pricing_info: {
          prices: familyPricing.prices, // Store all available pricing options
          duration_type: selectedPrice?.duration_type,
          duration_value: selectedPrice?.duration_value,
          duration_label: selectedPrice?.duration_label,
          miles: selectedPrice?.miles,
          battery_percent: selectedPrice?.battery_percent,
          location_name: selectedPrice?.location_name
        }
      };
    });

    setVehicleReservationItems(updatedItems);
    calculateFinalPrices(updatedItems);
    setIsCalculating(false);
  }

  const calculateFinalPrices = (items: Array<any>) => {
    let prices = {
      subtotal: 0,
      tax: 0,
      discount: 0,
      service_fee: 0,
      total: 0,
    }

    console.log('💰 calculateFinalPrices - items:', items);
    items.map((item:any)=>{
      let subtotal = item.price || 0;
      console.log('💰 Item price:', { family_id: item.family_id, price: item.price, subtotal });
      prices.subtotal += subtotal;
    });

    if(VehicleReservationMain.discount_code){
      const selectedDiscount:any = discounts.find((item:any) => {
        if (typeof item.code === 'string') {
          return item.code.toLowerCase() === VehicleReservationMain.discount_code.toLowerCase();
        }
        return false;
      });

      if(selectedDiscount){
        prices.discount = (Math.round(prices.subtotal * selectedDiscount.amount) / 100);
      } else { 
        prices.discount = 0;
      }
    }
    
    // Calculate service fee if enabled (after discount)
    if (storeDetails?.is_service_fee_enabled && storeDetails?.service_fee_percentage) {
      const serviceFeePercentage = parseFloat(storeDetails.service_fee_percentage) || 0;
      prices.service_fee = (prices.subtotal - prices.discount) * (serviceFeePercentage / 100); // + upcharge
    }
    
    prices.tax = (prices.subtotal - prices.discount + VehicleReservationMain.driver_tip + VehicleReservationMain.delivery_charge + prices.service_fee) * (storeDetails.sales_tax?storeDetails.sales_tax/100:0) || 0; // + upcharge
    prices.total = prices.subtotal - prices.discount + VehicleReservationMain.driver_tip + VehicleReservationMain.delivery_charge + prices.service_fee + prices.tax; // + upcharge

    // Include Bodhi Protect in subtotal only when not opted out
    if (!VehicleReservationMain.bodhi_protect_opted_out) {
      prices.total += (VehicleReservationMain.bodhi_protect_price || 0);
    }

    console.log('💰 Final calculated prices:', prices);
    setVehicleReservationValue('prices', prices);
  }

  // useEffect(() => {
  //   if(VehicleReservationMain.pickup){
  //     const priceTable = getPriceTableByBrandAndDate(priceLogicData, storeDetails.brand_id, VehicleReservationMain.pickup);
  //     setVehicleReservationValue('price_table_id', priceTable?.id??null);
  //   }
  // }, [priceLogicData, storeDetails.brand_id, VehicleReservationMain.pickup])

  useEffect(()=>{
    if(VehicleReservationMain.pickup && VehicleReservationMain.dropoff){
      const payload = {
        start_date: formatDateString(VehicleReservationMain.pickup),
        end_date: formatDateString(VehicleReservationMain.dropoff)
      }
      getAvaliableSheet(payload, (jsonRes:any, status, error) => {
        if(jsonRes) setVehicleReservationValue('availableSheet', jsonRes);
        else setVehicleReservationValue('availableSheet', null);
      })
    }else setVehicleReservationValue('availableSheet', null);
  }, [VehicleReservationMain.pickup, VehicleReservationMain.dropoff])

  // useEffect(() => {
  //   if(VehicleReservationMain.price_table_id){
  //     getHeaderData(VehicleReservationMain.price_table_id, (jsonRes:any, status, error) => {
  //       switch (status) {
  //         case 200:
  //           setVehicleReservationValue('headerData', jsonRes);
  //           break;
  //         default:
  //           setVehicleReservationValue('headerData', []);
  //           break;
  //       }
  //     });
  //     getTableData(VehicleReservationMain.price_table_id, (jsonRes:any, status, error) => {
  //       switch (status) {
  //         case 200:
  //           setVehicleReservationValue('priceTableData', jsonRes);
  //           break;
  //         default:
  //           setVehicleReservationValue('priceTableData', []);
  //           break;
  //       }
  //     });
  //   }else {
  //     setVehicleReservationValue('priceTableData', []);
  //   }
  // }, [VehicleReservationMain.price_table_id]);


  useEffect(()=>{
    // Pricing comes from category_id via new getBusyPeriods API
    // Only recalculate final prices (discount, tax, total) without calling old pricing API
    calculateFinalPrices(VehicleReservationItems);
  }, [
    VehicleReservationMain.driver_tip, 
    VehicleReservationMain.discount_code, 
    discounts, 
    storeDetails.sales_tax,
    VehicleReservationMain.bodhi_protect_opted_out,
    VehicleReservationMain.bodhi_protect_price,
    VehicleReservationMain.delivery_charge,
    storeDetails.is_service_fee_enabled,
    storeDetails.service_fee_percentage
  ]);

  const values: ContextProps = {
    isCalculating,
    VehicleReservationMain,
    VehicleReservationItems,
    setVehicleReservationMain,
    setVehicleReservationValue,
    getVehicleReservationValue,
    addVehicleReservationItems,
    removeVehicleReservationItem,
    clearVehicleReservationItems,
    initVehicleReservation: () => {
      setVehicleReservationMain(initializedVehicleMain);
      setVehicleReservationItems([]);
    },
    updatePricingForSelectedDuration,
  };

  return <VehicleReservationContext.Provider value={values}>{children}</VehicleReservationContext.Provider>;
};
